import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBriefcase, FiMapPin, FiClock, FiCalendar, FiAlertCircle, FiShare2 } from 'react-icons/fi';
import { useTheme } from '../../../context/ThemeContext';

// Smart description renderer: handles Quill HTML, literal-tag text, or plain text
const renderDescription = (desc) => {
    if (!desc) return '<p style="opacity:0.5;margin:0">No description provided.</p>';
    if (/<[a-zA-Z][\s\S]*?>/.test(desc)) return desc;
    return desc.replace(/\n/g, '<br/>');
};


const JobDetailModal = ({ show, onClose, job }) => {
    const { isDarkMode } = useTheme();

    if (!job) return null;

    const statusColors = {
        pending: { color: '#eab308', bg: 'rgba(234,179,8,0.1)', label: 'Pending Review' },
        approved: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Approved' },
        active: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Active' },
        rejected: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Rejected' },
    };

    const status = statusColors[job.status] || statusColors.pending;

    return (
        <AnimatePresence>
            {show && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        style={{
                            width: '100%',
                            maxWidth: '700px',
                            maxHeight: '85vh',
                            background: 'var(--bg-main)',
                            borderRadius: '32px',
                            border: '1px solid var(--border)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: isDarkMode ? '0 50px 100px -20px rgba(0,0,0,0.5)' : '0 50px 100px -20px rgba(0,0,0,0.1)'
                        }}
                    >
                        {/* Header */}
                        <div style={{ padding: '32px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: 'var(--bg-main)' }}>
                            <div>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                                    <span style={{ padding: '6px 14px', background: 'var(--accent-hover)', color: 'var(--accent)', borderRadius: 100, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {job.type}
                                    </span>
                                    <span style={{ padding: '6px 14px', background: status.bg, color: status.color, borderRadius: 100, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {status.label}
                                    </span>
                                </div>
                                <h2 style={{ fontSize: '28px', fontWeight: 900, color: 'var(--text-main)', margin: 0, letterSpacing: '-0.02em' }}>{job.title}</h2>
                                <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: '16px', fontWeight: 500 }}>{job.companyName}</p>
                            </div>
                            <motion.button
                                whileHover={{ scale: 1.1, background: 'var(--bg-surface)' }}
                                whileTap={{ scale: 0.9 }}
                                onClick={onClose}
                                style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--bg-card)', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                            >
                                <FiX size={20} />
                            </motion.button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '32px', overflowY: 'auto', flex: 1, color: 'var(--text-main)' }}>
                            {/* Rejection Alert */}
                            {job.status === 'rejected' && job.rejectionReason && (
                                <div style={{
                                    padding: '20px',
                                    background: 'rgba(239,68,68,0.05)',
                                    border: '1px solid rgba(239,68,68,0.2)',
                                    borderRadius: '20px',
                                    marginBottom: '32px',
                                    display: 'flex',
                                    gap: 16
                                }}>
                                    <FiAlertCircle size={24} style={{ color: '#ef4444', flexShrink: 0 }} />
                                    <div>
                                        <h4 style={{ color: '#ef4444', margin: '0 0 4px 0', fontSize: '15px', fontWeight: 800 }}>Admin Feedback</h4>
                                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '14px', lineHeight: 1.5 }}>{job.rejectionReason}</p>
                                    </div>
                                </div>
                            )}

                            {/* Info Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px', marginBottom: '40px' }}>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                        <FiMapPin size={18} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', opacity: 0.6 }}>Location</div>
                                        <div style={{ color: 'var(--text-main)', fontSize: '15px', fontWeight: 600 }}>{job.location} ({job.workMode})</div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                        <span style={{ fontSize: '18px', fontWeight: 700 }}>₹</span>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', opacity: 0.6 }}>Stipend/Salary</div>
                                        <div style={{ color: '#22c55e', fontSize: '15px', fontWeight: 700 }}>{job.stipend || 'Not specified'}</div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                        <FiClock size={18} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', opacity: 0.6 }}>Duration</div>
                                        <div style={{ color: 'var(--text-main)', fontSize: '15px', fontWeight: 600 }}>{job.duration || 'Not specified'}</div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                        <FiCalendar size={18} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', opacity: 0.6 }}>Posted On</div>
                                        <div style={{ color: 'var(--text-main)', fontSize: '15px', fontWeight: 600 }}>
                                            {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString() : 'Recent'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div style={{ marginBottom: '40px' }}>
                                <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <FiBriefcase size={18} style={{ color: 'var(--accent)' }} /> Job Description
                                </h3>
                                <style>{`
                                    .cdm-desc p { margin: 0 0 12px 0; color: var(--text-main); opacity: 0.85; line-height: 1.8; }
                                    .cdm-desc h1,.cdm-desc h2,.cdm-desc h3,.cdm-desc h4,.cdm-desc h5,.cdm-desc h6 {
                                        color: var(--text-main); font-weight: 800; letter-spacing: -0.02em;
                                        line-height: 1.3; margin: 18px 0 8px 0;
                                    }
                                    .cdm-desc h1{font-size:1.35rem}.cdm-desc h2{font-size:1.15rem}.cdm-desc h3{font-size:1rem}
                                    .cdm-desc strong,.cdm-desc b { color: var(--text-main); font-weight: 800; }
                                    .cdm-desc em,.cdm-desc i { font-style: italic; opacity: 0.9; }
                                    .cdm-desc ul { list-style: none; padding: 0; margin: 8px 0 16px 0; display: flex; flex-direction: column; gap: 6px; }
                                    .cdm-desc ul li { padding-left: 20px; position: relative; color: var(--text-main); opacity: 0.85; line-height: 1.7; }
                                    .cdm-desc ul li::before { content: '▸'; position: absolute; left: 0; color: var(--accent); font-size: 11px; top: 4px; }
                                    .cdm-desc ol { padding-left: 20px; margin: 8px 0 16px 0; display: flex; flex-direction: column; gap: 6px; }
                                    .cdm-desc ol li { color: var(--text-main); opacity: 0.85; line-height: 1.7; }
                                    .cdm-desc a { color: var(--accent); text-decoration: underline; text-underline-offset: 3px; }
                                    .cdm-desc hr { border: none; border-top: 1px solid var(--border); margin: 18px 0; }
                                    .cdm-desc blockquote { border-left: 3px solid var(--accent); padding-left: 14px; margin: 12px 0; opacity: 0.8; font-style: italic; }
                                    .cdm-desc code { background: var(--bg-card); padding: 2px 6px; border-radius: 6px; font-size: 13px; font-family: monospace; }
                                `}</style>
                                <div
                                    className="cdm-desc"
                                    style={{ color: 'var(--text-muted)', fontSize: '15px', lineHeight: 1.8 }}
                                    dangerouslySetInnerHTML={{ __html: renderDescription(job.description) }}
                                />
                            </div>

                            {/* Skills */}
                            {job.skills && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', marginBottom: '16px' }}>Required Skills</h3>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                        {(job.skills ? (typeof job.skills === 'string' ? job.skills.split(',') : (Array.isArray(job.skills) ? job.skills : [])) : []).map((skill, i) => {
                                            const skillName = typeof skill === 'string' ? skill.trim() : String(skill || '');
                                            if (!skillName) return null;
                                            return (
                                                <span key={i} style={{ padding: '8px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-main)', fontSize: '14px', fontWeight: 600 }}>
                                                    {skillName}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '24px 32px', background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                            <button
                                onClick={() => {
                                    const url = `${window.location.origin}/job-portal/opportunities?job=${job.id}`;
                                    const title = `${job.title} at ${job.companyName}`;
                                    const text = `Check out this opportunity: ${job.title} at ${job.companyName}`;
                                    if (navigator.share) {
                                        navigator.share({ title, text, url }).catch(() => { });
                                    } else {
                                        navigator.clipboard.writeText(url);
                                    }
                                }}
                                style={{ padding: '12px 24px', background: 'var(--accent-hover)', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                                <FiShare2 size={16} /> Share
                            </button>
                            <button
                                onClick={onClose}
                                style={{ padding: '12px 24px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                Close View
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default JobDetailModal;
